import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";

import { Icon } from "../Icon";
import LpgDiscontinueOutline from "../../icons/Lpg/DiscontinueOutline";
import LpgContinueOutline from "../../icons/Lpg/ContinueOutline";

import styles from "./ComparisonTable.scss";

const directionValues = ["left", "right"] as const;
type DirectionValue = typeof directionValues[number];

const positionVariantValues = [
  "hardware",
  "hardware-dense",
  "product",
  "product-hero-feature",
] as const;
type PositionVariantValue = typeof positionVariantValues[number];

type ComparisonTableSliderButtonBaseProps = {
  direction?: DirectionValue;
  positionVariant: PositionVariantValue;
  isHidden?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

export type ComparisonTableSliderButtonProps = ComparisonTableSliderButtonBaseProps &
  Omit<
    React.ComponentPropsWithoutRef<"button">,
    keyof ComparisonTableSliderButtonBaseProps
  >;

const defaultProps = {
  direction: "left",
  isHidden: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClick: () => {},
} as const;

const validatedDefaultProps: ReactDefaultProps<
  ComparisonTableSliderButtonBaseProps,
  typeof defaultProps
> = defaultProps;

const DirectionIcon = {
  left: LpgDiscontinueOutline,
  right: LpgContinueOutline,
};

export const ComparisonTableSliderButton: ((
  props: ComparisonTableSliderButtonProps
) => JSX.Element | null) &
  ReactComponentStaticProps<ComparisonTableSliderButtonProps> = ({
  direction = defaultProps.direction,
  isHidden = defaultProps.isHidden,
  onClick = defaultProps.onClick,
  positionVariant,
  ...restProps
}) => {
  return (
    <button
      className={cx(styles["slider-button"], {
        [styles["slider-button--hidden"]]: isHidden,
        [styles["slider-button--left"]]: direction === "left",
        [styles["slider-button--right"]]: direction === "right",
        [styles["slider-button--position-hardware"]]:
          positionVariant === "hardware",
        [styles["slider-button--position-hardware-dense"]]:
          positionVariant === "hardware-dense",
        [styles["slider-button--position-product"]]:
          positionVariant === "product",
        [styles["slider-button--position-product-hero-feature"]]:
          positionVariant === "product-hero-feature",
      })}
      type="button"
      onClick={onClick}
      {...restProps}
    >
      <span className={styles["slider-button-icon"]}>
        <Icon icon={DirectionIcon[direction]} size={Icon.Size.parent} />
      </span>
    </button>
  );
};

ComparisonTableSliderButton.propTypes = {
  positionVariant: PropTypes.oneOf(positionVariantValues).isRequired,
  direction: PropTypes.oneOf(directionValues),
  isHidden: PropTypes.bool,
  onClick: PropTypes.func,
};

ComparisonTableSliderButton.defaultProps = validatedDefaultProps;

ComparisonTableSliderButton.displayName = "ComparisonTable.SliderButton";

export default ComparisonTableSliderButton;
